import { Component, Suspense, lazy, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Loader2, LocateFixed, X } from 'lucide-react';
import { Button } from '../ui/button';
import { useIsMobile } from '../../hooks/use-mobile';
import ModelControlsHelp from '../crag/ModelControlsHelp';
import { isDrawableModelLine } from '../crag/useModelLine';
import type { CragRouteModelRoute } from '../crag/CragRouteModel';
import { cn } from '../../lib/utils';
import type { Climb } from '../../types/climb';
import type { ModelView } from '../../types/crag-route';
import { CragRouteLineNav } from './CragRouteLineNav';
import { cragModelFraming } from './crag-model-framing';

// three.js and the model: only somebody who opens a sector pays for them.
const CragRouteModel = lazy(() => import('../crag/CragRouteModel'));

/** Above this a model is not downloaded unasked: under a cliff, signal is what is scarce. */
const AUTO_LOAD_BYTES = 15 * 1024 * 1024;

/**
 * Pixels the camera keeps clear at the bottom, so a route's start is not under what
 * sits there: the phone's bottom bar, and the selected route's card above it.
 */
const BOTTOM_CLEARANCE = {
  phone: { selected: 170, none: 72 },
  desktop: { selected: 130, none: 0 },
};

/** Glass over the rock: legible on it whatever the theme. */
const OVERLAY_BUTTON = 'border border-white/20 bg-black/50 text-white hover:bg-black/70 hover:text-white';

/** A chunk that did not arrive, which at a crag is a real possibility, is a failed model. */
class ChunkBoundary extends Component<{ onError: () => void; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    this.props.onError();
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

/** Whether a model of this size may be fetched without asking. Unknown means no reason to ask. */
function modelLoadsByItself(byteSize: number | null | undefined) {
  return (byteSize ?? 0) <= AUTO_LOAD_BYTES;
}

/** Which of the two ways of reading a sector is on screen. */
export function CragViewSwitch({
  view,
  onChange,
  className,
}: {
  view: 'model' | 'photo';
  onChange: (view: 'model' | 'photo') => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const options = [
    { id: 'model', label: t('cragProfile.model3d.viewModel') },
    { id: 'photo', label: t('cragProfile.model3d.viewPhoto') },
  ] as const;

  return (
    <div role="group" aria-label={t('cragProfile.model3d.viewSwitch')} className={cn('flex rounded-md p-0.5', OVERLAY_BUTTON, 'hover:bg-black/50', className)}>
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          aria-pressed={view === option.id}
          onClick={() => onChange(option.id)}
          className={cn(
            'rounded px-2.5 py-1 text-xs font-medium transition-colors',
            view === option.id ? 'bg-white text-black' : 'text-white/80 hover:text-white',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

interface CragModelStageProps {
  url: string;
  byteSize: number | null;
  /** Survives a trip to the Info tab, so nobody is asked twice. */
  loadRequested: boolean;
  onLoadRequested: () => void;
  /** The tab: its routes go with it, and the camera moves when it changes. */
  sectorKey: string;
  /** Where the sector opens, and the limits on where it can be turned to from there. */
  sectorView: ModelView | null;
  climbs: Climb[];
  selectedClimbId: string | null;
  onSelect: (climbId: string) => void;
  onDeselect: () => void;
  showStanding: boolean;
  onFailed: () => void;
  /** Top right, over the model: the view switch and whatever else the page puts there. */
  controls: ReactNode;
  /** The selected route's row. */
  card: ReactNode;
}

/**
 * A sector read on the crag's 3D model: the model, the routes as a line nav on the
 * left, the camera's controls on the right and, once a route is selected, its row
 * at the bottom. The page's floating button stays bottom right, so the card and the
 * model both leave it room.
 */
export function CragModelStage({
  url,
  byteSize,
  loadRequested,
  onLoadRequested,
  sectorKey,
  sectorView,
  climbs,
  selectedClimbId,
  onSelect,
  onDeselect,
  showStanding,
  onFailed,
  controls,
  card,
}: CragModelStageProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  // Labels on a phone: out of the way as soon as the model is what is being looked
  // at, back for a new list of routes. A desktop has the room to leave them.
  const [foldedIn, setFoldedIn] = useState<string | null>(null);
  const folded = isMobile && foldedIn === sectorKey;
  const fold = () => setFoldedIn(sectorKey);

  // Adjusted while rendering, so the camera's new request is in the same frame as
  // the selection that caused it.
  const [request, setRequest] = useState({ selected: selectedClimbId, count: 0, recentred: false });
  if (request.selected !== selectedClimbId) {
    setRequest({ selected: selectedClimbId, count: request.count + 1, recentred: false });
  }

  const bottomInset = BOTTOM_CLEARANCE[isMobile ? 'phone' : 'desktop'][selectedClimbId ? 'selected' : 'none'];
  const framing = useMemo(() => {
    const where = cragModelFraming({
      sectorKey,
      sectorView,
      climbs,
      selectedClimbId,
      request: request.count,
      recentred: request.recentred,
    });
    return where && { ...where, bottomInset };
  }, [sectorKey, sectorView, climbs, selectedClimbId, request.count, request.recentred, bottomInset]);

  const routes = useMemo(
    () =>
      climbs.flatMap((climb): CragRouteModelRoute[] =>
        isDrawableModelLine(climb.model_line)
          ? [{ id: climb.id, name: climb.name ?? '', model_line: climb.model_line }]
          : [],
      ),
    [climbs],
  );

  const selected = climbs.find((climb) => climb.id === selectedClimbId) ?? null;
  const showModel = loadRequested || modelLoadsByItself(byteSize);

  const toggle = (climbId: string) => {
    if (climbId === selectedClimbId) {
      onDeselect();
      return;
    }
    onSelect(climbId);
    // Picked: now it is the model's turn to be looked at.
    fold();
  };

  return (
    // Dark in either theme: the nav and the controls are white, for the rock.
    <div data-testid="crag-model-stage" className="relative size-full overflow-hidden bg-zinc-900">
      {/* In the capture phase: the orbit's listeners must still get the press. */}
      <div className="absolute inset-0" onPointerDownCapture={fold}>
        {showModel ? (
          <ChunkBoundary onError={onFailed}>
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center text-white/70">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              }
            >
              <CragRouteModel
                url={url}
                routes={routes}
                editor={null}
                framing={framing}
                selectedRouteId={selectedClimbId}
                viewLimits={sectorView}
                onSelectRoute={onSelect}
                onError={onFailed}
                showControlsHelp={false}
                className="h-full rounded-none border-0 bg-transparent text-white/70"
              />
            </Suspense>
          </ChunkBoundary>
        ) : (
          <div className="flex h-full items-center justify-center p-6">
            <Button variant="secondary" className="gap-2" onClick={onLoadRequested}>
              <Box className="h-4 w-4" />
              {t('cragProfile.model3d.load', { size: Math.round((byteSize ?? 0) / (1024 * 1024)) })}
            </Button>
          </div>
        )}
      </div>

      <div className="pointer-events-none absolute inset-0 flex flex-col">
        <div className="flex min-h-0 flex-1 items-start">
          {climbs.length > 0 ? (
            <CragRouteLineNav
              climbs={climbs}
              activeClimbId={selectedClimbId}
              onToggle={toggle}
              showStanding={showStanding}
              folded={folded}
              onUnfold={() => setFoldedIn(null)}
              className="max-h-full"
            />
          ) : (
            <p className="m-3 rounded-md bg-black/50 px-3 py-1.5 text-sm text-white">
              {t('cragProfile.sectorHasNoClimbs')}
            </p>
          )}

          {/* Over the nav's row rather than beside it: as a sibling it would squeeze the
              nav, whose labels have their own limit. */}
          <div className="pointer-events-auto absolute right-0 top-0 z-10 flex flex-col items-end gap-2 p-2">
            {controls}
            {showModel && (
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('cragProfile.model3d.recentre')}
                title={t('cragProfile.model3d.recentre')}
                onClick={() => setRequest({ ...request, count: request.count + 1, recentred: true })}
                className={cn('h-9 w-9', OVERLAY_BUTTON)}
              >
                <LocateFixed className="h-4 w-4" />
              </Button>
            )}
            {showModel && (
              <ModelControlsHelp
                canPan={!sectorView?.limits || sectorView.limits.pan > 0}
                canPivot={!sectorView?.limits}
                className={OVERLAY_BUTTON}
              />
            )}
          </div>
        </div>

        {/* Beside the floating button rather than under it, and clear of the phone's
            bottom bar, which the model runs behind. */}
        {selected && (
          <div
            data-testid="crag-model-card"
            className="pointer-events-auto relative mb-[calc(4.5rem+env(safe-area-inset-bottom))] ml-3 mr-[5.25rem] max-w-xl shrink-0 rounded-lg border bg-background shadow-lg md:mb-6"
          >
            <button
              type="button"
              aria-label={t('cragProfile.model3d.deselect')}
              onClick={onDeselect}
              className="absolute -right-3 -top-3 z-10 flex h-6 w-6 items-center justify-center rounded-full border bg-background text-muted-foreground shadow hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
            {!isDrawableModelLine(selected.model_line) && (
              <p className="border-b px-3 py-1 text-[11px] text-muted-foreground">{t('cragProfile.model3d.noLine')}</p>
            )}
            <div className="overflow-hidden rounded-lg">{card}</div>
          </div>
        )}
      </div>
    </div>
  );
}
